import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Instagram, Facebook, Mail, MessageCircle, Sparkles, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, track } from "../../lib/institutional";
import { api, formatApiErrorDetail } from "../../lib/api";

const COLS = [
  { title: "Descubra", links: [
    { label: "Restaurantes", to: "/restaurantes-em-joao-pessoa" },
    { label: "Hotéis", to: "/hoteis-em-joao-pessoa" },
    { label: "Praias", to: "/praias-de-joao-pessoa" },
    { label: "Eventos", to: "/eventos-em-joao-pessoa" },
    { label: "Turismo", to: "/turismo-em-joao-pessoa" },
    { label: "Empresas", to: "/empresas-em-joao-pessoa" },
  ]},
  { title: "Empresas", links: [
    { label: "Cadastrar empresa", to: "/minha-empresa" },
    { label: "Planos", to: "/planos" },
    { label: "Painel", to: "/painel" },
    { label: "Para Empresas", to: "/para-empresas" },
  ]},
  { title: "Institucional", links: [
    { label: "Sobre", to: "/sobre" },
    { label: "Como Funciona", to: "/como-funciona" },
    { label: "Contato", to: "/contato" },
    { label: "Trabalhe conosco", to: "/trabalhe-conosco" },
    { label: "Termos de Uso", to: "/termos-de-uso" },
    { label: "Privacidade", to: "/politica-de-privacidade" },
  ]},
  { title: "Aplicativo", links: [
    { label: "Acessar o App", to: "/feed" },
    { label: "Perguntar à IA", to: "/ia" },
    { label: "Mapa", to: "/mapa" },
  ]},
];

export default function SiteFooter() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const subscribeNews = async (e) => {
    e.preventDefault();
    setSubLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      setSubscribed(true);
      setEmail("");
      toast.success("Inscrição confirmada! Você receberá as novidades por e-mail.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível inscrever agora.");
    } finally { setSubLoading(false); }
  };

  return (
    <footer data-testid="site-footer" className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div data-testid="newsletter-bar" className="rounded-3xl bg-gradient-to-br from-primary to-[hsl(213_90%_50%)] p-6 md:p-8 mb-12 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div className="max-w-md">
            <p className="font-heading font-bold text-white text-xl md:text-2xl">Receba as novidades de João Pessoa</p>
            <p className="text-sm text-white/85 mt-1">Eventos, dicas e o melhor da cidade direto no seu e-mail. Sem spam.</p>
          </div>
          {subscribed ? (
            <p data-testid="newsletter-success" className="text-white font-semibold text-sm bg-white/15 px-4 py-3 rounded-2xl">✓ Você está inscrito! Fique de olho no seu e-mail.</p>
          ) : (
            <form onSubmit={subscribeNews} className="flex w-full md:w-auto gap-2 min-w-0">
              <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu melhor e-mail"
                className="flex-1 min-w-0 md:w-72 h-12 px-4 rounded-2xl bg-white/95 text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-white" />
              <button data-testid="newsletter-submit" type="submit" disabled={subLoading}
                className="h-12 px-4 sm:px-5 shrink-0 rounded-2xl bg-white text-primary font-semibold flex items-center gap-2 disabled:opacity-70 press whitespace-nowrap">
                {subLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Inscrever
              </button>
            </form>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
          <div className="col-span-2">
            <img src="/logo-h.webp" alt="Guia de Jampa AI" width="200" height="105" className="h-[62px] w-auto object-contain mb-4" />
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              A plataforma inteligente que conecta pessoas, empresas e experiências em João Pessoa.
            </p>
            <div className="flex items-center gap-3 mt-5">
              {CONTACT.instagram && <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><Instagram className="w-4 h-4" /></a>}
              {CONTACT.facebook && <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><Facebook className="w-4 h-4" /></a>}
              {CONTACT.whatsapp && <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><MessageCircle className="w-4 h-4" /></a>}
              <a href={`mailto:${CONTACT.email}`} aria-label="E-mail" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><Mail className="w-4 h-4" /></a>
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="font-heading font-semibold text-white text-sm mb-4">{col.title}</p>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-sm text-slate-400 hover:text-white transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[11px] text-slate-500 leading-relaxed">
            <p className="font-semibold text-slate-400 tracking-wide">GUIA DE JAMPA AI TECHNOLOGY LTDA</p>
            <p>CNPJ 69.307.733/0001-70</p>
            <p>Av. Paulista, 1471, Conj. 511, Sala 02 — Bela Vista — São Paulo/SP · CEP 01311-300</p>
            <p className="mt-2">© {new Date().getFullYear()} Guia de Jampa AI · João Pessoa/PB. Todos os direitos reservados.</p>
          </div>
          <button data-testid="footer-open-app" onClick={() => { track("open_app", { from: "footer" }); navigate("/feed"); }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-brand-gradient px-4 py-2 rounded-full press">
            <Sparkles className="w-4 h-4" /> Acessar o App
          </button>
        </div>
      </div>
    </footer>
  );
}
